<script>
import { get, post, destroy } from '@rails/request.js';

export default {
  name: 'VolunteerSubscriptionButton',
  props: {
    volunteerId: Number,
    subscriptionUrl: String,
  },
  data() {
    return {
      notificationPermission: this.browserSupportsWebPush() ? Notification.permission : undefined,
      isSubscribed: false,
      subscription: null,
    };
  },
  mounted() {
    this.checkSubscribed();
  },
  methods: {
    browserSupportsWebPush() {
      // Some browsers still don't support the WebPush API.
      return (typeof Notification !== 'undefined')
    },
    unsupportedBrowserMessage() {
      alert(this.$i18n.t('subscription.unsupported'))
    },
    hasPermission() {
      return this.notificationPermission === 'granted';
    },
    async requestPermission() {
      if (this.notificationPermission === 'default') {
        this.notificationPermission = await Notification.requestPermission();
      }
      return this.notificationPermission === 'granted';
    },
    async getVapidPublicKey() {
      const meta = document.querySelector('meta[name="web-push-public-key"]');
      if (!meta) throw new Error('VAPID public key not found');
      return meta.content;
    },
    async getPushSubscription() {
      if (this.subscription) return this.subscription;
      const registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
      let sub = await registration.pushManager.getSubscription();
      if (sub) {
        this.subscription = sub;
        return sub;
      }
      try {
        const applicationServerKey = await this.getVapidPublicKey();
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey
        });
        this.subscription = sub;
        return sub;
      } catch (e) {
        console.error('Web push subscription failed', e);
        return null;
      }
    },
    async getSubscriptionBody() {
      const sub = await this.getPushSubscription();
      if (!sub) return null;
      const { endpoint, keys: { p256dh, auth } } = sub.toJSON();
      return { endpoint, p256dh_key: p256dh, auth_key: auth };
    },
    async checkSubscribed() {
      if (!this.hasPermission()) {
        this.isSubscribed = false;
        return;
      }
      const body = await this.getSubscriptionBody();
      if (!body) {
        this.isSubscribed = false;
        return;
      }
      const response = await get(this.subscriptionUrl, { query: body });
      this.isSubscribed = response.ok && response.statusCode !== 204;
    },
    async subscribe() {
      if (!await this.requestPermission()) return;

      const body = await this.getSubscriptionBody();
      if (!body) return;

      const response = await post(this.subscriptionUrl, { body });
      if (response.ok) this.isSubscribed = true;
    },
    async unsubscribe() {
      if (!this.hasPermission()) return;

      const body = await this.getSubscriptionBody();
      if (!body) return;

      const response = await destroy(this.subscriptionUrl, { query: body });
      if (response.ok) this.isSubscribed = false;
    },
  }
};
</script>

<template>
  <span v-if="!this.browserSupportsWebPush()" class="btn btn-sm btn-disabled-secondary border border" @click="this.unsupportedBrowserMessage()">
    {{ this.$i18n.t('subscription.unsubscribed') }}
    <span class="bi bi-question-circle"></span>
  </span>
  <span v-else-if="isSubscribed" class="btn btn-sm btn-secondary" @click="unsubscribe">
    <span class="bi bi-bell-fill"></span>
    {{ this.$i18n.t('subscription.subscribed') }}
  </span>
  <span v-else class="btn btn-sm btn-outline-secondary" @click="subscribe">
    <span class="bi bi-bell-slash"></span>
    {{ this.$i18n.t('subscription.unsubscribed') }}
  </span>
</template>
